import type { AnyFieldApi } from "@tanstack/react-form";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { PillBtn } from "#/components/primitives";
import { Field } from "#/components/ui/field";
import { Input } from "#/components/ui/input";
import {
  customerDisplayName,
  isEmailVerified,
  login,
  type UnverifiedInfo,
} from "#/lib/auth-client";
import { attachCartToCustomer } from "#/lib/medusa-types/commerce";
import { notify } from "#/lib/notify";
import { ROUTES } from "#/lib/routes";
import { sessionKeys, sessionQueries, useSession } from "#/lib/useSession";
import { registerUserFn } from "#/server/functions/auth-email";

const searchSchema = z.object({
  redirect: z.string().optional(),
  verified: z.enum(["success", "failed"]).optional(),
});

const loginSchema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

type LoginValues = z.infer<typeof loginSchema>;

const AUTH_FIELD_CLASS = "h-14 px-6";

const FLATTEN_AUTH_ERRORS = false;

function isNetworkError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return /network|failed to fetch|connection|offline/i.test(err.message);
}

function flatLoginError(err: unknown): string {
  if (isNetworkError(err)) return "Check your connection and try again.";
  return "Sign in failed. Please try again.";
}

function fieldErrorText(field: AnyFieldApi): string | null {
  if (!field.state.meta.isTouched) return null;
  const errs = field.state.meta.errors;
  if (errs.length === 0) return null;
  return errs
    .map((e) => (typeof e === "string" ? e : (e?.message ?? "Invalid")))
    .join(", ");
}

export const Route = createFileRoute("/login")({
  validateSearch: searchSchema,
  head: () => ({ meta: [{ title: "Sign In" }] }),
  beforeLoad: async ({ context, search }) => {
    const session = await context.queryClient.ensureQueryData(
      sessionQueries.current(),
    );
    if (session.customer && isEmailVerified(session.customer)) {
      throw redirect({ to: search.redirect ?? ROUTES.account });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const { customer } = useSession();
  const [unverified, setUnverified] = useState<UnverifiedInfo | null>(null);

  const unverifiedInfo: UnverifiedInfo | null =
    unverified ??
    (customer && !isEmailVerified(customer)
      ? {
          customerId: customer.id,
          email: customer.email,
          name: customerDisplayName(customer),
        }
      : null);

  const resendMutation = useMutation({
    mutationFn: (info: UnverifiedInfo) =>
      registerUserFn({
        data: {
          email: info.email,
          customerId: info.customerId,
          customerName: info.name,
        },
      }),
    onSuccess: () =>
      notify.success("Verification email sent", {
        description: "Check your inbox for the new link.",
      }),
    onError: () =>
      notify.error("Could not send email", {
        description: "Please try again in a moment.",
      }),
  });

  useEffect(() => {
    if (search.verified === "success") {
      notify.success("Email verified", {
        description: "Your email is confirmed. Please sign in.",
      });
    } else if (search.verified === "failed") {
      notify.error("Verification failed", {
        description: "This link is invalid or has expired.",
      });
    }
  }, [search.verified]);

  const mutation = useMutation({
    mutationFn: (values: LoginValues) => login(values),
    onSuccess: async (result) => {
      if (result.kind === "redirect") {
        window.location.href = result.location;
        return;
      }
      if (result.kind === "unverified") {
        setUnverified({
          customerId: result.customerId,
          email: result.email,
          name: result.name,
        });
        setErrorMessage(null);
        return;
      }
      queryClient.setQueryData(sessionKeys.current, {
        customer: result.customer,
      });
      try {
        await attachCartToCustomer(result.customer.id);
      } catch {
        // Cart attach is best-effort — the user is logged in either way.
      }
      notify.success(
        `Welcome back, ${result.customer.first_name ?? ""}`.trim(),
      );
      navigate({ to: search.redirect ?? ROUTES.account });
    },
    onError: (err) => {
      if (import.meta.env.DEV) {
        console.log("[login] error", err);
      }
      if (FLATTEN_AUTH_ERRORS) {
        setErrorMessage(flatLoginError(err));
        return;
      }
      setErrorMessage(
        err instanceof Error
          ? err.message
          : "Sign in failed. Please try again.",
      );
    },
  });

  const form = useForm({
    defaultValues: { email: "", password: "" } satisfies LoginValues,
    validators: { onChange: loginSchema },
    onSubmit: ({ value }) => {
      setErrorMessage(null);
      return mutation.mutateAsync(value);
    },
  });

  return (
    <div data-screen-label="Login" className="pb-24">
      <section className="mx-auto w-full max-w-md px-6 pt-16 md:pt-24">
        <h1 className="m-0 mb-3 font-bold font-display text-4xl text-lp-green-dk leading-none tracking-tight">
          Welcome Back
        </h1>
        <p className="mt-2 mb-10 m-0 font-display text-lp-text">
          Sign in to track orders, manage your wishlist, and more.
        </p>

        {unverifiedInfo && (
          <div className="mb-8 rounded-2xl border border-lp-green bg-lp-green-soft p-5 font-display text-lp-text">
            <p className="m-0 font-semibold text-lp-green-dk">
              Verify your email to continue
            </p>
            <p className="mt-1 mb-4 text-sm">
              We sent a verification link to {unverifiedInfo.email}. Please
              confirm your email, then sign in.
            </p>
            <PillBtn
              type="button"
              variant="green"
              size="sm"
              disabled={resendMutation.isPending}
              onClick={() => resendMutation.mutate(unverifiedInfo)}
            >
              {resendMutation.isPending
                ? "Sending…"
                : "Resend verification email"}
            </PillBtn>
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="flex flex-col gap-5"
          noValidate
        >
          <form.Field name="email">
            {(field) => {
              const errText = fieldErrorText(field);
              return (
                <div>
                  <Field label="Email">
                    {(id) => (
                      <Input
                        id={id}
                        type="email"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        autoComplete="email"
                        aria-invalid={errText !== null}
                        className={AUTH_FIELD_CLASS}
                      />
                    )}
                  </Field>
                  {errText && (
                    <p
                      role="alert"
                      className="mt-1 font-display text-destructive text-sm"
                    >
                      {errText}
                    </p>
                  )}
                </div>
              );
            }}
          </form.Field>

          <form.Field name="password">
            {(field) => {
              const errText = fieldErrorText(field);
              return (
                <div>
                  <Field label="Password">
                    {(id) => (
                      <div className="relative">
                        <Input
                          id={id}
                          type={showPassword ? "text" : "password"}
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          autoComplete="current-password"
                          aria-invalid={errText !== null}
                          className={`${AUTH_FIELD_CLASS} pr-12`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          aria-pressed={showPassword}
                          tabIndex={-1}
                          className="absolute inset-y-0 right-4 my-auto flex h-6 w-6 cursor-pointer items-center justify-center border-0 bg-transparent text-lp-text-lt hover:text-lp-green-dk"
                        >
                          {showPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    )}
                  </Field>
                  {errText && (
                    <p
                      role="alert"
                      className="mt-1 font-display text-destructive text-sm"
                    >
                      {errText}
                    </p>
                  )}
                </div>
              );
            }}
          </form.Field>

          {errorMessage && (
            <div className="font-display text-destructive text-sm">
              {errorMessage}
            </div>
          )}

          <div className="mt-2 flex items-center justify-between">
            <Link
              to={ROUTES.forgotPassword}
              className="font-display text-lp-green-mid text-sm underline hover:text-lp-green-dk"
            >
              Forgot password?
            </Link>
            <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting]}>
              {([canSubmit, isSubmitting]) => (
                <PillBtn
                  type="submit"
                  variant="green"
                  size="sm"
                  disabled={!canSubmit || isSubmitting || mutation.isPending}
                >
                  {mutation.isPending || isSubmitting
                    ? "Signing in…"
                    : "Sign In"}
                </PillBtn>
              )}
            </form.Subscribe>
          </div>
        </form>

        <div className="mt-10 font-display text-lp-text">
          New to Learning Plus?{" "}
          <Link
            to={ROUTES.register}
            search={{ redirect: search.redirect }}
            className="text-lp-green-dk underline hover:text-lp-green-mid"
          >
            Create an account
          </Link>
        </div>
      </section>
    </div>
  );
}
